"use client";

import CaseStudyCard from "@/components/ui/case-studies/CaseStudyCard";
import CumulativeStats from "@/components/ui/case-studies/CumulativeStats";
import WelcomeSection from "@/components/ui/case-studies/WelcomeSection";
import { caseStudiesData } from "@/data/caseStudies";
import { useTranslations } from "next-intl";

export default function CaseStudiesContent() {
    const t = useTranslations("CaseStudiesPage.case_studies_section");
    const tc = useTranslations("CaseStudiesPage.case_study_cards");

    // Build translated case study data for each card, preserving numeric metric values from source data
    const translatedCaseStudies = [
        {
            ...caseStudiesData[0],
            title: tc("case_01.title"),
            subtitle: tc("case_01.subtitle"),
            chipLabel: tc("case_01.chip_label"),
            contents: [
                { title: tc("case_01.content_0_title"), descriptions: [] },
                { title: tc("case_01.content_1_title"), descriptions: [tc("case_01.content_1_desc_0")] },
                { title: tc("case_01.content_2_title"), descriptions: [tc("case_01.content_2_desc_0"), tc("case_01.content_2_desc_1"), tc("case_01.content_2_desc_2")] },
                { title: tc("case_01.content_3_title"), descriptions: [tc("case_01.content_3_desc_0")] },
            ],
            metrics: caseStudiesData[0].metrics.map((m, i) => ({ ...m, label: tc(`case_01.metric_${i}_label`) })),
        },
        {
            ...caseStudiesData[1],
            title: tc("case_02.title"),
            subtitle: tc("case_02.subtitle"),
            chipLabel: tc("case_02.chip_label"),
            contents: [
                { title: tc("case_02.content_0_title"), descriptions: [] },
                { title: tc("case_02.content_1_title"), descriptions: [tc("case_02.content_1_desc_0")] },
                { title: tc("case_02.content_2_title"), descriptions: [tc("case_02.content_2_desc_0"), tc("case_02.content_2_desc_1"), tc("case_02.content_2_desc_2"), tc("case_02.content_2_desc_3")] },
                { title: tc("case_02.content_3_title"), descriptions: [tc("case_02.content_3_desc_0"), tc("case_02.content_3_desc_1")] },
            ],
            metrics: caseStudiesData[1].metrics.map((m, i) => ({ ...m, label: tc(`case_02.metric_${i}_label`) })),
        },
        {
            ...caseStudiesData[2],
            title: tc("case_03.title"),
            subtitle: tc("case_03.subtitle"),
            chipLabel: tc("case_03.chip_label"),
            contents: [
                { title: tc("case_03.content_0_title"), descriptions: [] },
                { title: tc("case_03.content_1_title"), descriptions: [tc("case_03.content_1_desc_0"), tc("case_03.content_1_desc_1")] },
                { title: tc("case_03.content_2_title"), descriptions: [tc("case_03.content_2_desc_0"), tc("case_03.content_2_desc_1"), tc("case_03.content_2_desc_2"), tc("case_03.content_2_desc_3")] },
                { title: tc("case_03.content_3_title"), descriptions: [tc("case_03.content_3_desc_0"), tc("case_03.content_3_desc_1")] },
            ],
            metrics: caseStudiesData[2].metrics.map((m, i) => ({ ...m, label: tc(`case_03.metric_${i}_label`) })),
        },
        {
            ...caseStudiesData[3],
            title: tc("case_04.title"),
            subtitle: tc("case_04.subtitle"),
            chipLabel: tc("case_04.chip_label"),
            contents: [
                { title: tc("case_04.content_0_title"), descriptions: [] },
                { title: tc("case_04.content_1_title"), descriptions: [tc("case_04.content_1_desc_0")] },
                { title: tc("case_04.content_2_title"), descriptions: [tc("case_04.content_2_desc_0"), tc("case_04.content_2_desc_1"), tc("case_04.content_2_desc_2")] },
                { title: tc("case_04.content_3_title"), descriptions: [tc("case_04.content_3_desc_0"), tc("case_04.content_3_desc_1")] },
            ],
            metrics: caseStudiesData[3].metrics.map((m, i) => ({ ...m, label: tc(`case_04.metric_${i}_label`) })),
        },
    ];

    return (
        <div className="min-h-screen bg-[#f8fafc] pb-24">
            <WelcomeSection />
            {/* Main Content */}
            <div className="mx-auto max-w-[1200px] px-4 md:px-0">
                <div className="text-center mb-16 mt-16">
                    <span className="inline-block bg-[#F15859] text-white text-xs font-bold px-6 py-1.5 rounded-full mb-6 shadow-sm">
                        {t('chip')}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[#132c4b] mb-4">
                        {t('title')}
                    </h2>
                    <p className="text-[#64748b] max-w-[90%] mx-auto text-base md:text-lg">
                        {t('description')}
                    </p>
                </div>
                <div className="flex flex-col gap-4">
                    {translatedCaseStudies.map((study, index) => (
                        <CaseStudyCard key={index} {...study} />
                    ))}
                </div>

                {/* Cumulative Stats */}
                <CumulativeStats />
            </div>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": caseStudiesData.map((study) => ({
                            "@type": "Article",
                            "@id": `https://airxpress.jp/case-studies/${study.slug}#article`,
                            "headline": study.title,
                            "description": study.subtitle,
                            "image": `https://airxpress.jp${study.image}`,
                            "url": `https://airxpress.jp/case-studies/${study.slug}`,
                            "inLanguage": "ja",
                            "mainEntityOfPage": {
                                "@type": "WebPage",
                                "@id": `https://airxpress.jp/case-studies/${study.slug}`
                            },
                            "author": {
                                "@type": "Organization",
                                "name": "AirXpress",
                                "url": "https://airxpress.jp"
                            },
                            "publisher": {
                                "@type": "Organization",
                                "name": "AirXpress",
                                "logo": {
                                    "@type": "ImageObject",
                                    "url": "https://airxpress.jp/images/og-logo-color.webp"
                                }
                            },
                            "about": {
                                "@type": "Service",
                                "name": "民泊運営代行"
                            }
                        }))
                    })
                }}
            />
        </div>
    );
}
